import { AnchorButton, ButtonGroup, Tooltip } from '@blueprintjs/core';
import { useApplicationDetail } from '@axo/shared/data-access/hooks';
import { Brand } from '@axo/config/@types/Brand.types';
import { MarketCountryCode } from '@axo/shared/types';
import { useContext } from 'react';
import { DataAccessContext } from '@axo/shared/data-access/provider';
import { AppToaster } from '../../AppToaster';
import { buildPublicMyPageUrl, type MyPageEnv } from './buildPublicMyPageUrl';

// vite modes: production | staging | development (test under vitest)
const env = import.meta.env.MODE as MyPageEnv;

// This is for retrocompatibility with application that don't have a brand specified
const defaultBrands: Record<MarketCountryCode, Brand | undefined> = {
  [MarketCountryCode.Finland]: 'axolaina.fi',
  [MarketCountryCode.Denmark]: 'lendme.dk',
  [MarketCountryCode.Norway]: 'axofinans.no',
  [MarketCountryCode.Sweden]: 'axofinans.se',
};

interface GoToMyPageButtonProps {
  applicationID: string;
  brand?: Brand;
}

export const GoToMyPageButton: React.FC<GoToMyPageButtonProps> = ({
  applicationID,
  brand,
}) => {
  const application = useApplicationDetail(applicationID);
  const {
    state: {
      user: { clientID },
    },
  } = useContext(DataAccessContext);

  if (!brand && application.data?.MarketCountry) {
    brand = defaultBrands[application.data.MarketCountry as MarketCountryCode];
  }

  const myPageLink = {
    brand,
    product: application.data?.Product,
    magicToken: application.data?.MagicToken,
    env,
    clientId: clientID,
  };
  const myPageURL = buildPublicMyPageUrl({ ...myPageLink, traffic: 'internal' });
  const publicMyPageURL = buildPublicMyPageUrl(myPageLink);

  const copyPublicLink = () => {
    if (!publicMyPageURL) return;
    window.navigator.clipboard.writeText(publicMyPageURL).then(
      () =>
        AppToaster.then((toaster) =>
          toaster.show({
            message: 'Public customer link copied to clipboard',
            intent: 'success',
            icon: 'clipboard',
          })
        ),
      () =>
        AppToaster.then((toaster) =>
          toaster.show({
            message: 'Failed to copy link — check clipboard permissions',
            intent: 'danger',
            icon: 'error',
          })
        )
    );
  };

  return (
    <ButtonGroup>
      <AnchorButton
        disabled={!myPageURL}
        icon="open-application"
        target="_blank"
        href={myPageURL ?? undefined}
        rel="noopener noreferrer"
      >
        Go to MyPage
      </AnchorButton>
      <Tooltip
        placement="bottom"
        content={
          publicMyPageURL
            ? 'Copy customer link (public — safe to share)'
            : 'No public MyPage link for this brand'
        }
      >
        <AnchorButton
          icon="link"
          aria-label="Copy customer link"
          disabled={!publicMyPageURL}
          onClick={copyPublicLink}
        />
      </Tooltip>
    </ButtonGroup>
  );
};
